import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import { useCreateIntegration } from "../../hooks/mutation/create/useCreateIntegration";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import z from "zod";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { AlertSchemaPicker } from "../AlertSchemaPicker";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { mergeParams } from "../../utils/url";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";

const schema = Schemas.integration
  .omit({ id: true, metadata: true, url: true, auth: true })
  .extend({ system: z.boolean().default(false) });

type FormValues = z.input<typeof schema>;

const NewIntegrationDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: createIntegration, isPending: isCreating } = useCreateIntegration();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const { data: alertProviders = [] } = useAlertProviders();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      alertSchemaId: "",
      system: false,
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setSubmitError(null);
      const created = await createIntegration({
        ...data,
        system: data.system ?? false,
      });
      reset({});
      onDialogClosed({ message: "Integration created!", severity: "success" });

      const selectedProvider = alertProviders.find(
        (provider) => provider.id === data.alertSchemaId,
      );
      if (selectedProvider?.system === true || data.alertSchemaId.startsWith("system-")) {
        navigate(
          `${pathname}?${mergeParams(
            searchParams,
            new URLSearchParams({
              dialog: "setup-integration",
              integrationId: created.id,
            }),
          ).toString()}`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    setSubmitError(null);
    reset({ name: "", description: "", alertSchemaId: "", system: false });
    onDialogClosed();
  };

  const onInvalid = (errs: Record<string, unknown>) => {
    console.error(errs);
    setSubmitError("Error during submit");
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle
        title={"New Integration"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="new-integration-form"
        onSubmit={handleSubmit(onSubmit, onInvalid)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a integration and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Sentry organization integration"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.alertSchemaId}>
                <FormLabel>Alert schema</FormLabel>

                <Controller
                  name="alertSchemaId"
                  control={control}
                  render={({ field }) => (
                    <AlertSchemaPicker
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e);
                        if (submitError) { 
                          setSubmitError(null) 
                        };
                      }}
                    />
                  )}
                />

                <FormHelperText>
                  {errors.alertSchemaId?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Create"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewIntegrationDialog;
